import {
  createApiClient,
  type ApiClient,
  type ExtraHeadersProvider,
  type LanguageProvider,
} from "@roastery/api-client";
import { createContext, useContext, useMemo, type ReactNode } from "react";
import { useAuth } from "react-oidc-context";

const ApiContext = createContext<ApiClient | null>(null);

/**
 * Publishes an API client bound to the current access token. Silent renewal swaps the token, which
 * rebuilds the (cheap) client; query caches are keyed by method and path, so nothing is refetched.
 *
 * <p>`getExtraHeaders` is read per request rather than baked into the client, which is what lets the
 * roastery's application add `X-Support-Session` (US-14.3) without the client — and the cache with it —
 * being rebuilt when a session starts or ends.
 */
export function ApiProvider({
  baseUrl,
  children,
  getExtraHeaders,
  getLanguage,
}: {
  baseUrl: string;
  children: ReactNode;
  getExtraHeaders?: ExtraHeadersProvider;
  /** The language the screens are being read in; goes out as `Accept-Language` (US-23.4). */
  getLanguage?: LanguageProvider;
}) {
  const auth = useAuth();
  const accessToken = auth.user?.access_token;

  const client = useMemo(
    () => createApiClient({ baseUrl, getAccessToken: () => accessToken, getExtraHeaders, getLanguage }),
    [baseUrl, accessToken, getExtraHeaders, getLanguage],
  );

  return <ApiContext.Provider value={client}>{children}</ApiContext.Provider>;
}

export function useApi(): ApiClient {
  const client = useContext(ApiContext);
  if (!client) {
    throw new Error("useApi must be used inside <RoasteryAuthProvider>");
  }
  return client;
}
